import * as Yup from 'yup';

export const MATCH_TYPES = [
  'T10',
  'T20',
  'TEST',
  'THE_HUNDRED',
  'ODI',
  'OTHER_LIMITED_OVER',
  'INDOOR',
] as const;
export type MatchType = (typeof MATCH_TYPES)[number];

export const BALL_TYPES = [
  'BallType.tennis',
  'BallType.leather',
  'BallType.other',
] as const;
export type BallType = (typeof BALL_TYPES)[number];

export const TOURNAMENT_CATEGORIES = [
  'OPEN',
  'CORPORATE',
  'COMMUNITY',
  'COLLEGE',
  'SCHOOL',
  'SERIES',
  'UNIVERSITY',
  'OTHER',
] as const;
export type TournamentCategory = (typeof TOURNAMENT_CATEGORIES)[number];

/** Central label maps (import these in any component) */
export const MatchTypeLabel = {
  T10: 'T10',
  T20: 'T20',
  TEST: 'TEST',
  THE_HUNDRED: 'THE HUNDRED',
  ODI: 'ODI',
  OTHER_LIMITED_OVER: 'OTHER LIMITED OVERS',
  INDOOR: 'INDOOR',
} as const satisfies Record<MatchType, string>;

export const BallTypeLabel = {
  'BallType.tennis': 'Tennis',
  'BallType.leather': 'Leather',
  'BallType.other': 'Other',
} as const satisfies Record<BallType, string>;

export const TournamentCategoryLabel = {
  OPEN: 'Open',
  CORPORATE: 'Corporate',
  COMMUNITY: 'Community',
  COLLEGE: 'College',
  SCHOOL: 'School',
  SERIES: 'Series',
  UNIVERSITY: 'University',
  OTHER: 'Other',
} as const satisfies Record<TournamentCategory, string>;

/** Optional helpers (nice for previews) */
export const getMatchTypeLabel = (v?: MatchType | null) => (v ? MatchTypeLabel[v] : '—');
export const getBallTypeLabel = (v?: BallType | null) => (v ? BallTypeLabel[v] : '—');
export const getTournamentCategoryLabel = (v?: TournamentCategory | null) =>
  (v ? TournamentCategoryLabel[v] : '—');


export interface ICreateTournament {
  tournamentName: string;
  leagueId: string;
  description: string;
  organizerName: string;
  organizerPhoneNumber: string;
  organizerPhoneNumberCountryCode: string;
  organizerEmailId: string;
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
  matchType: MatchType;
  maxOversPerBowler: number;
  pointForWin: number;
  pointForTie: number;
  pointForAbandoned: number;
  ballType: BallType;
  tournamentCategory: TournamentCategory;
  bannerUri?: string | null;     // optional
  bannerBase64?: string | null;  // raw base64 (no data: prefix), used when sending as multipart
  bannerFileType?: string | null;
  bannerFileName?: string | null;
  maxOvers?: number;             // optional + conditional
  maxWickets?: number;           // optional + conditional
  isRosterLocked: boolean;
  canEditScoreCard: boolean;
  canEditTournament: boolean | null; // required, but can be null
  isImpactPlayerAllowed: boolean;
  bonusPointsEnabled: boolean;
  isStrictModeEnabled: boolean;
  isTickerAutomationEnabled: boolean;
}

export const FIELD_LIMITS = {
  tournamentName: 80,
  organizerName: 60,
  organizerEmail: 254,
  description: 500,
} as const;

export type ICreateTournamentUI =
  Omit<ICreateTournament, 'leagueId' | 'bannerUri'> & {
    leagueId: string | null;
    bannerUri: string | null;
  };

/** ========= Validation Schema ========= */
export const schema: Yup.ObjectSchema<ICreateTournamentUI> = Yup.object({
  tournamentName: Yup.string()
    .trim()
    .max(FIELD_LIMITS.tournamentName, `Must be at most ${FIELD_LIMITS.tournamentName} characters`)
    .required('Tournament name is required'),

  // leagueId provided later → nullable + default(null)
  leagueId: Yup.mixed<string>()
    .nullable(true)
    .transform((v) => (v === '' ? null : v))
    .default(null),

  description: Yup.string()
    .trim()
    .max(FIELD_LIMITS.description, `Must be at most ${FIELD_LIMITS.description} characters`)
    .required('Description is required'),

  organizerName: Yup.string()
    .trim()
    .max(FIELD_LIMITS.organizerName, `Must be at most ${FIELD_LIMITS.organizerName} characters`)
    .required('Organizer name is required'),
  organizerPhoneNumberCountryCode: Yup.string()
    .matches(/^\d+$/, 'Invalid country code')
    .required('Country code is required'),
  organizerPhoneNumber: Yup.string()
    .matches(/^\d+$/, 'Phone must be digits only')
    .min(7, 'Too short')
    .required('Phone number is required'),
  organizerEmailId: Yup.string()
    .email('Invalid email')
    .max(FIELD_LIMITS.organizerEmail, `Must be at most ${FIELD_LIMITS.organizerEmail} characters`)
    .required('Email is required'),

  startDate: Yup.string().required('Start date is required'),
  endDate: Yup.string()
    .required('End date is required')
    .test('after-start', 'End date must be after start date', function (value) {
      const { startDate } = this.parent as ICreateTournamentUI;
      return !!value && !!startDate && new Date(value) >= new Date(startDate);
    }),

  matchType: Yup.mixed<MatchType>().oneOf([...MATCH_TYPES]).required('Match type is required'),

  maxOversPerBowler: Yup.number().typeError('Enter a number').integer().min(1, 'Must be >= 1').required('Required'),
  pointForWin: Yup.number().typeError('Enter a number').integer().min(0).required('Required'),
  pointForTie: Yup.number().typeError('Enter a number').integer().min(0).required('Required'),
  pointForAbandoned: Yup.number().typeError('Enter a number').integer().min(0).required('Required'),

  ballType: Yup.mixed<BallType>().oneOf([...BALL_TYPES]).required('Ball type is required'),
  tournamentCategory: Yup.mixed<TournamentCategory>()
    .oneOf([...TOURNAMENT_CATEGORIES])
    .required('Tournament category is required'),

  // bannerUri provided later → nullable + default(null)
  bannerUri: Yup.string().nullable(true).transform((v) => (v === '' ? null : v)).default(null),

  maxOvers: Yup.number()
    .typeError('Enter a number')
    .integer()
    .min(1, 'Must be >= 1')
    .when('matchType', {
      is: 'OTHER_LIMITED_OVER',
      then: (s) => s.required('Max overs is required for Other Limited Over'),
      otherwise: (s) => s.strip().optional(),
    }),

  maxWickets: Yup.number()
    .typeError('Enter a number')
    .integer()
    .min(1, 'Must be >= 1')
    .when('matchType', {
      is: 'OTHER_LIMITED_OVER',
      then: (s) => s.required('Max wickets is required for Other Limited Over'),
      otherwise: (s) => s.strip().optional(),
    }),

  isRosterLocked: Yup.boolean().required(),
  canEditScoreCard: Yup.boolean().required(),
  canEditTournament: Yup.boolean().nullable(),
  isImpactPlayerAllowed: Yup.boolean().required(),
  bonusPointsEnabled: Yup.boolean().required(),
  isStrictModeEnabled: Yup.boolean().required(),
  isTickerAutomationEnabled: Yup.boolean().required(),
}) as Yup.ObjectSchema<ICreateTournamentUI>;
